import React, { useEffect, useRef, useState } from 'react';
import { ISoldToTableData } from '../../actions/create-order/get-sold-tos.action';
import submitProductRequestAction, {
    IProductRequestFormInput,
    SubmitProductRequestInput
} from '../../actions/create-order/submit-product-request.action';
import { getTomorrowDate } from '../../utilities/format-date';
import { ErrorBoundary } from '../../utilities/global-error-boundary';
import { getCountryDisplayName } from '../../utilities/map-country-name';
import {
    ILxsProductRequestModalData,
    IProductRequestCapabilityItem,
    IProductRequestFormState,
    IProductRequestModalContentProps
} from './lxs-product-request-modal.data';
import moduleDefinition from './lxs-product-request-modal.definition.json';
import { ILxsProductRequestModalProps, ILxsProductRequestModalResources } from './lxs-product-request-modal.props.autogenerated';
import ProductRequestModalContentView from './lxs-product-request-modal.view';

const _defaultResources = (Object.fromEntries(
    Object.entries(moduleDefinition.resources).map(([k, v]) => [k, (v as { value: string }).value])
) as unknown) as ILxsProductRequestModalResources;

export const ProductRequestModalContent: React.FC<IProductRequestModalContentProps> = ({
    resources: resourcesProp,
    onClose,
    actionContext,
    user,
    soldTos,
    initialSoldToId
}) => {
    const locale = actionContext.requestContext?.locale ?? 'en';
    const resources: ILxsProductRequestModalResources = resourcesProp ?? _defaultResources;
    const defaultSoldTo = (initialSoldToId ? soldTos?.find(soldToEntry => soldToEntry.Id === initialSoldToId) : undefined) ?? soldTos?.[0];

    const [selectedSoldToId, setSelectedSoldToId] = useState(defaultSoldTo?.Id ?? '');
    const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const isAuthenticated = actionContext.requestContext?.user?.isAuthenticated ?? false;
    const getCountryLabel = (code: string) => getCountryDisplayName(code, locale);

    const companyDropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target as Node)) {
                setIsCompanyDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const [form, setForm] = useState<IProductRequestFormState>({
        molecule: '',
        quantity: '',
        deliveryDate: '',
        deliveryDateISO: '',
        targetPrice: '',
        additionalInfo: '',
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        company: defaultSoldTo?.Name1 ?? '',
        country: defaultSoldTo?.Country ?? '',
        email: user?.emailAddress || '',
        phone: ''
    });

    const handleChange = (field: keyof IProductRequestFormState) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const value = e.target.value;
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSoldToSelect = (soldTo: ISoldToTableData) => {
        setSelectedSoldToId(soldTo.Id);
        setForm(prev => ({ ...prev, company: soldTo.Name1, country: soldTo.Country ?? '' }));
        setIsCompanyDropdownOpen(false);
    };

    const openDeliveryDatePicker = (e: React.MouseEvent<HTMLInputElement>) => {
        const target = e.currentTarget as HTMLInputElement & { showPicker?: () => void };
        target.showPicker?.();
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isoValue = e.target.value;
        if (isoValue) {
            const [year, month, day] = isoValue.split('-');
            const display = `${day}.${month}.${year}`;
            setForm(prev => ({ ...prev, deliveryDate: display, deliveryDateISO: isoValue }));
        } else {
            setForm(prev => ({ ...prev, deliveryDate: '', deliveryDateISO: '' }));
        }
    };

    const isSubmitDisabled = !form.molecule || !form.quantity || !form.deliveryDate || !form.email || isSubmitting;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSubmitDisabled) {
            return;
        }
        setIsSubmitting(true);
        setSubmitError(null);

        const deliveryDateISO = form.deliveryDateISO ? new Date(`${form.deliveryDateISO}T12:00:00.000Z`).toISOString() : '';

        const formInput: IProductRequestFormInput = {
            FirstName: form.firstName,
            LastName: form.lastName,
            Email: form.email,
            Company: form.company,
            Country: form.country,
            Phone: form.phone,
            TargetCompound: form.molecule,
            RequiredQty: form.quantity,
            DeliveryDate: deliveryDateISO,
            TargetPrice: form.targetPrice,
            AdditionalInformation: form.additionalInfo,
            Language: locale
        };

        try {
            await submitProductRequestAction(new SubmitProductRequestInput(formInput), actionContext);
            setIsSubmitted(true);
        } catch {
            setSubmitError(resources.productRequestModalSubmitError);
            setIsSubmitting(false);
        }
    };

    const capabilities: IProductRequestCapabilityItem[] = [
        {
            title: resources.productRequestModalCapabilityCropProtectionTitle,
            text: resources.productRequestModalCapabilityCropProtectionText,
            linkText: resources.productRequestModalCapabilityCropProtectionLinkText,
            linkUrl: resources.productRequestModalCapabilityCropProtectionLinkUrl
        },
        {
            title: resources.productRequestModalCapabilityPharmaTitle,
            text: resources.productRequestModalCapabilityPharmaText,
            linkText: resources.productRequestModalCapabilityPharmaLinkText,
            linkUrl: resources.productRequestModalCapabilityPharmaLinkUrl
        },
        {
            title: resources.productRequestModalCapabilitySpecialtyChemicalsTitle,
            text: resources.productRequestModalCapabilitySpecialtyChemicalsText,
            linkText: resources.productRequestModalCapabilitySpecialtyChemicalsLinkText,
            linkUrl: resources.productRequestModalCapabilitySpecialtyChemicalsLinkUrl
        },
        {
            title: resources.productRequestModalCapabilityTechnologiesTitle,
            text: resources.productRequestModalCapabilityTechnologiesText,
            linkText: resources.productRequestModalCapabilityTechnologiesLinkText,
            linkUrl: resources.productRequestModalCapabilityTechnologiesLinkUrl
        },
        {
            title: resources.productRequestModalCapabilityServicesTitle,
            text: resources.productRequestModalCapabilityServicesText,
            linkText: resources.productRequestModalCapabilityServicesLinkText,
            linkUrl: resources.productRequestModalCapabilityServicesLinkUrl
        }
    ];

    return (
        <ProductRequestModalContentView
            resources={resources}
            onClose={onClose}
            soldTos={soldTos}
            isAuthenticated={isAuthenticated}
            selectedSoldToId={selectedSoldToId}
            isCompanyDropdownOpen={isCompanyDropdownOpen}
            companyDropdownRef={companyDropdownRef}
            form={form}
            isSubmitting={isSubmitting}
            submitError={submitError}
            isSubmitted={isSubmitted}
            isSubmitDisabled={isSubmitDisabled}
            capabilities={capabilities}
            minSelectableDeliveryDate={getTomorrowDate()
                .toISOString()
                .substring(0, 10)}
            getCountryDisplayName={getCountryLabel}
            handleChange={handleChange}
            handleSoldToSelect={handleSoldToSelect}
            openDeliveryDatePicker={openDeliveryDatePicker}
            handleDateChange={handleDateChange}
            handleSubmit={handleSubmit}
            setIsCompanyDropdownOpen={setIsCompanyDropdownOpen}
        />
    );
};

class LxsProductRequestModal extends React.PureComponent<ILxsProductRequestModalProps<ILxsProductRequestModalData>> {
    public render(): JSX.Element {
        return (
            <ProductRequestModalContent
                resources={this.props.resources}
                actionContext={this.props.context.actionContext}
                user={this.props.context.request.user}
            />
        );
    }
}

export default ErrorBoundary(LxsProductRequestModal, 'lxs-product-request-modal');
